import { LineStyle } from 'lightweight-charts'
import { log as debugLog, warn as debugWarn } from '@/utils/logger'
import { toUtcSeconds } from '@/utils/utcTime'
import BadgeComponent from './Badge.jsx'
import PanelComponent from './Panel.jsx'
import { BoxFillSeries } from './BoxFillSeries.js'
import { LabelOverlaySeries } from './LabelOverlaySeries.js'

// ─── Helpers de color ───────────────────────────────────────────────────────

export function hexToRgba(hex, alpha) {
  if (!hex || hex === 'transparent') return 'transparent'
  if (hex.startsWith('rgba(') || hex.startsWith('rgb(')) return hex
  const h = hex.startsWith('#') ? hex.slice(1) : hex
  if (h.length < 6) return hex
  const r = parseInt(h.slice(0, 2), 16)
  const g = parseInt(h.slice(2, 4), 16)
  const b = parseInt(h.slice(4, 6), 16)
  const a = alpha !== undefined ? alpha : (h.length >= 8 ? parseInt(h.slice(6, 8), 16) / 255 : 1)
  return `rgba(${r},${g},${b},${a.toFixed(3)})`
}

export function withAlpha(color, alpha) {
  const a = Math.max(0, Math.min(1, alpha))
  if (typeof color !== 'string') return `rgba(176,141,87,${a})`
  if (color.startsWith('rgba(')) {
    const parts = color.match(/rgba\((\d+),\s*(\d+),\s*(\d+),\s*([\d.]+)\)/)
    if (parts) return `rgba(${parts[1]},${parts[2]},${parts[3]},${a})`
    return color
  }
  if (color.startsWith('rgb(')) {
    const parts = color.match(/rgb\((\d+),\s*(\d+),\s*(\d+)\)/)
    if (parts) return `rgba(${parts[1]},${parts[2]},${parts[3]},${a})`
    return color
  }
  if (color.startsWith('#')) {
    return hexToRgba(color, a)
  }
  return color
}

function lineStyleFromString(style) {
  switch (style) {
    case 'dashed': return LineStyle.Dashed
    case 'dotted': return LineStyle.Dotted
    case 'largeDashed': return LineStyle.LargeDashed
    case 'sparseDotted': return LineStyle.SparseDotted
    case 'solid':
    default: return LineStyle.Solid
  }
}

// Conversor ÚNICO compartido (@/utils/utcTime, v1.3.2): acepta segundos,
// milisegundos (payloads /charting/*), strings 'YYYY-MM-DD' (TFs diarios+,
// donde los bar.time llegan como business-day strings) y business-day objects
// de LWC. Antes había dos conversores propios (toSeconds solo números,
// timeToSeconds con new Date) y los times string de 1D/1W/1M pasaban CRUDOS
// por cleanTime → comparaciones número-vs-string en NaN/silencio (p. ej. la
// caja de señal: cleanTime(lastTime) string → Math.max(...) = NaN → span
// vacío → no se pintaba nada en diario+). Contrato externo intacto: null para
// lo no interpretable, segundos UTC en cualquier otro caso.
function cleanTime(t) {
  return toUtcSeconds(t)
}

function cleanValue(v) {
  if (v == null || Number.isNaN(v) || !Number.isFinite(v)) return null
  return v
}

function timeToSeconds(t) {
  return toUtcSeconds(t)
}

function getVisibleTimeRange(chart, series) {
  try {
    const timeScale = chart.timeScale()
    const logicalRange = timeScale.getVisibleLogicalRange()
    if (!logicalRange) return null
    const data = series && typeof series.data === 'function' ? series.data() : []
    if (!data.length) return null
    const fromIdx = Math.max(0, Math.floor(logicalRange.from))
    const toIdx = Math.min(data.length - 1, Math.ceil(logicalRange.to))
    if (fromIdx > toIdx || fromIdx >= data.length) return null
    return { from: timeToSeconds(data[fromIdx].time), to: timeToSeconds(data[toIdx].time) }
  } catch (e) {
    return null
  }
}

function overlapsVisibleRange(start, end, range) {
  if (!range) return true
  const s = timeToSeconds(start)
  const e = timeToSeconds(end)
  if (s == null || e == null) return false
  return s <= range.to && e >= range.from
}

function isInVisibleRange(t, range) {
  if (!range) return true
  const x = cleanTime(t)
  if (x == null) return false
  return x >= range.from && x <= range.to
}

function sortByTimeDesc(arr, key = 'time') {
  return [...arr].sort((a, b) => (cleanTime(b[key]) ?? 0) - (cleanTime(a[key]) ?? 0))
}

function limitByType(items, maxPerType) {
  const byType = {}
  for (const item of items) {
    const type = item.type || 'default'
    if (!byType[type]) byType[type] = []
    byType[type].push(item)
  }
  const out = []
  for (const type of Object.keys(byType)) {
    const max = maxPerType[type] ?? maxPerType.default ?? Infinity
    out.push(...byType[type].slice(0, Math.max(0, max)))
  }
  return out
}

function barIntervalSeconds(candles) {
  if (!Array.isArray(candles) || candles.length < 2) return 3600
  const t0 = timeToSeconds(candles[0].time)
  const t1 = timeToSeconds(candles[1].time)
  if (t0 == null || t1 == null) return 3600
  return Math.max(1, t1 - t0)
}

// ─── Datos externos (inyectados desde ChartPublicPage) ───────────────────────

let externalData = null
let externalSignal = null
let externalVersion = 0

export function setExternalData(data, signal) {
  externalData = data
  externalSignal = signal
  externalVersion++
}

export function getExternalData() {
  return externalData
}

export function getExternalSignal() {
  return externalSignal
}

export function getExternalVersion() {
  return externalVersion
}

// ─── Configuración por defecto ────────────────────────────────────────────────

export const defaultConfig = {
  _version: 4,

  // ── Lógica / Estructura ──────────────────────────────────────────────────
  structure: {
    showSwingStruct: true,
    showInternalStruct: true,
    showSwingLabels: true,
    showStrongWeak: true,
    swingLen: 10,
    internalLen: 5,
    minMoveATRMult: 0.5,
    maxHistoryEvents: 5,
    maxInternalEvents: 3,
    maxSwingLabels: 10,
  },

  // ── FVG ──────────────────────────────────────────────────────────────────
  fvg: {
    showFVG: true,
    showIFVG: true,
    showMidline: true,
    showLabel: true,
    maxFVG: 10,
    maxIFVG: 5,
    maxVisibleBoxes: 10,
    alphaFill: 0.18,
    extensionBars: 5,
    minSizeATRMult: 0.5,
    filterEnabled: true,
  },

  // ── Order Blocks ─────────────────────────────────────────────────────────
  ob: {
    showOB: true,
    showMidline: true,
    showLabel: true,
    showGrade: true,
    maxPerTf: 10,
    maxVisibleBoxes: 10,
    alphaFill: 0.18,
    lookback: 30,
    extensionBars: 10,
    mitigation: 'wick',
    minVolumeGrade: 1.2,
  },

  // ── Fibonacci ────────────────────────────────────────────────────────────
  fib: {
    showFib: true,
    showOTE: true,
    showLabels: true,
    anchoredLabels: true,
  },

  // ── Multi-Timeframe ──────────────────────────────────────────────────────
  multiTimeframe: {
    enableHTF: true,
    htfResolution: '',
    htfEMALen: 50,
    requireHTFAlignment: false,
  },

  // ── Señal ─────────────────────────────────────────────────────────────────
  signal: {
    showSignal: true,
    showSL: true,
    showTP1: true,
    showTP2: true,
    showTP3: true,
    showTP4: true,
    showTP5: true,
    minScore: 4,
    minRR: 1.2,
    extensionBars: 10,
  },

  // ── Estilo Quantum ───────────────────────────────────────────────────────
  colorBull: '#1B5E20',
  colorBear: '#B71C1C',
  colorNeutral: '#374151',
  colorCHoCH: '#B71C1C',
  colorIFVG: '#C2410C',
  colorOTE: '#B45309',
  colorOB: '#1D4ED8',
  colorFvgBear: '#C2410C',
  lineWidth: 1.5,
  cloudOpacity: 0.18,
  boxAlpha: 0.18,
  markerSize: 2,
  haloOpacity: 0.25,
  labelFont: "'Playfair Display', Georgia, serif",
  labelBgOpacity: 0.12,
  plaqueStyle: 'plain',

  // ── Dashboard ────────────────────────────────────────────────────────────
  showDashboard: true,
  dashboardMinimized: false,
  badgePosition: { x: 8, y: 8 },
  panelSections: {},
}

// ─── detect() ─────────────────────────────────────────────────────────────────

export function detect(candles, config) {
  const t0 = typeof performance !== 'undefined' ? performance.now() : 0
  if (!externalData) {
    const t1 = typeof performance !== 'undefined' ? performance.now() : 0
    // eslint-disable-next-line no-console
    debugLog(`[ict_quantum] detect empty: ${(t1 - t0).toFixed(2)}ms`)
    return null
  }
  const result = {
    ...externalData,
    signal: externalSignal,
    _dataVersion: externalVersion,
  }
  const t1 = typeof performance !== 'undefined' ? performance.now() : 0
  // eslint-disable-next-line no-console
  debugLog(`[ict_quantum] detect from external: ${(t1 - t0).toFixed(2)}ms`)
  return result
}

function emptyResult() {
  return null
}

// ─── render() ─────────────────────────────────────────────────────────────────

export function render(chart, candleSeries, result, config, api) {
  const t0 = typeof performance !== 'undefined' ? performance.now() : 0
  if (!result || !result.boxes) return
  const cfg = { ...defaultConfig, ...(config || {}) }

  // ── Debug hook (window.__ICT_DEBUG__) ──────────────────────────────────────
  // Expone conteos crudos (lo que envio el backend) y renderizados (lo que
  // realmente se dibujo tras filtros de config/rango visible). Existe porque
  // el sintoma original del bug ("FVG/OB pintan pero HH/HL/LH, CHoCH y fib
  // no salen") es invisible para un test que solo mira las CountBar del
  // badge (OBs/FVG/Sesgo siempre renderizan su texto, incluso con valor 0).
  const countBy = (arr, key) => {
    const out = {}
    for (const item of arr || []) {
      const k = item?.[key]
      if (k == null) continue
      out[k] = (out[k] || 0) + 1
    }
    return out
  }
  const ictDebug = {
    rawLabelTypes: countBy(result.labels, 'text'),
    rawLineTypes: countBy(result.lines, 'type'),
    rawBoxTypes: countBy(result.boxes, 'type'),
    renderedSwingLabels: 0,
    renderedLines: { swing: 0, internal: 0, fib: 0, strongweak: 0, other: 0 },
    renderedBoxes: {},
    dashboard: result.dashboard || null,
    timestamp: Date.now(),
  }

  // Guarda anti-mutación de velas
  if (candleSeries && typeof candleSeries.data === 'function') {
    candleSeries.__ictqCandleCountBeforeRender = candleSeries.data().length
  }

  const theme = {
    bull: cfg.colorBull || '#10b981',
    bear: cfg.colorBear || '#ef4444',
    neutral: cfg.colorNeutral || '#475569',
    choch: cfg.colorCHoCH || '#dc2626',
    ifvg: cfg.colorIFVG || '#f59e0b',
    ote: cfg.colorOTE || '#475569',
    ob: cfg.colorOB || '#3b82f6',
    fvgBear: cfg.colorFvgBear || '#f97316',
  }

  const fcfg = cfg.fvg || {}
  const ocfg = cfg.ob || {}
  const scfg = cfg.structure || {}
  const fibcfg = cfg.fib || {}
  const sigcfg = cfg.signal || {}

  const font = cfg.labelFont || "'Playfair Display', Georgia, serif"
  const labelBgOpacity = cfg.labelBgOpacity ?? 0.12
  const plaqueStyle = cfg.plaqueStyle || 'plaque'
  const lineWidth = cfg.lineWidth ?? 1.5

  const allCandles = candleSeries && candleSeries.data ? candleSeries.data() : []
  const lastCandle = allCandles.length > 0 ? allCandles[allCandles.length - 1] : null
  const lastTime = lastCandle ? lastCandle.time : null

  // ── Cajas (FVG / OB / IFVG / OTE) ──────────────────────────────────────────
  const tBoxes0 = typeof performance !== 'undefined' ? performance.now() : 0
  let visibleRange = null
  try {
    visibleRange = getVisibleTimeRange(chart, candleSeries)
  } catch {}

  // (no se usan filtros de visibilidad por coordenadas: el canvas overlay
  // descarta en el propio renderer, evitando que el primer frame vacíe labels.)
  const labelsByTime = {}
  const addLabel = (opts) => {
    const t = opts?.time
    if (t == null || Number.isNaN(t)) return
    if (!labelsByTime[t]) labelsByTime[t] = []
    labelsByTime[t].push(opts)
  }

  const candidateBoxes = []
  for (const b of result.boxes || []) {
    const left = cleanTime(b.left)
    const right = cleanTime(b.right)
    const top = cleanValue(b.top)
    const bottom = cleanValue(b.bottom)
    if (left == null || right == null || top == null || bottom == null) continue
    if (!overlapsVisibleRange(left, right, visibleRange)) continue
    // Zonas mitigadas/llenas no se pintan para reducir ruido histórico.
    if (b.mitigated || b.filled) continue

    const type = b.type
    const dir = b.direction
    let visible = false
    let color = theme.neutral
    let alpha = cfg.boxAlpha ?? 0.18
    let borderColor = color
    let borderWidth = 0
    let label = null
    let labelColor = color
    let showMidline = false
    let midline = null

    if (type === 'FVG') {
      if (fcfg.showFVG === false) continue
      visible = true
      color = dir === 'bull' ? theme.bull : theme.fvgBear
      alpha = fcfg.alphaFill ?? 0.25
      borderColor = color
      borderWidth = 1
      label = dir === 'bull' ? 'BISI' : 'SIBI'
      labelColor = color
      showMidline = fcfg.showMidline !== false && b.show_midline
      midline = b.midline
    } else if (type === 'IFVG') {
      if (fcfg.showIFVG === false) continue
      visible = true
      color = theme.ifvg
      alpha = (fcfg.alphaFill ?? 0.25) * 0.5
      borderColor = color
      borderWidth = 1
      label = 'IFVG'
      labelColor = color
    } else if (type === 'OB') {
      if (ocfg.showOB === false) continue
      visible = true
      color = theme.ob
      alpha = ocfg.alphaFill ?? 0.25
      borderColor = color
      borderWidth = 1
      label = b.grade ? `${b.timeframe} OB ${b.grade}` : `${b.timeframe} OB`
      labelColor = color
      showMidline = ocfg.showMidline !== false && b.show_midline
      midline = b.midline
    } else if (type === 'OTE') {
      if (fibcfg.showOTE === false) continue
      visible = true
      color = theme.ote
      alpha = 0.25
      borderColor = color
      borderWidth = 1
      label = 'OTE'
      labelColor = color
    }

    if (!visible) continue
    candidateBoxes.push({ left, right, top, bottom, color, alpha, borderColor, borderWidth, label, labelColor, showMidline, midline, type, dir })
  }

  // Limitar densidad: solo los N más recientes de cada tipo dentro del rango visible.
  const maxPerBoxType = {
    FVG: fcfg.maxVisibleBoxes ?? 10,
    IFVG: fcfg.maxVisibleBoxes ?? 5,
    OB: ocfg.maxVisibleBoxes ?? 10,
    OTE: 5,
  }
  const sortedBoxes = sortByTimeDesc(candidateBoxes, 'right')
  const limitedBoxes = []
  const boxCountByType = {}
  for (const b of sortedBoxes) {
    const max = maxPerBoxType[b.type] ?? Infinity
    boxCountByType[b.type] = (boxCountByType[b.type] || 0) + 1
    if (boxCountByType[b.type] > max) continue
    limitedBoxes.push(b)
  }
  ictDebug.renderedBoxes = countBy(limitedBoxes, 'type')

  for (const b of limitedBoxes) {
    const { left, right, top, bottom, color, alpha, borderColor, borderWidth, label, labelColor, showMidline, midline, type, dir } = b
    const seriesData = [
      { time: left, upper: top, lower: bottom, fillColor: color, color, alpha, borderColor, borderWidth, borderStyle: 'solid' },
      { time: right, upper: top, lower: bottom, fillColor: color, color, alpha, borderColor, borderWidth, borderStyle: 'solid' },
    ]
    const boxSeries = api.addCustomSeries(new BoxFillSeries(), {
      color: 'rgba(0,0,0,0)',
      fillColor: 'rgba(0,0,0,0)',
      lineWidth: 0,
      lastValueVisible: false,
      priceLineVisible: false,
      borderWidth,
      borderStyle: 'solid',
      alpha,
      autoscaleInfoProvider: () => null,
    })
    if (boxSeries) boxSeries.setData(seriesData)

    if (label && (type === 'FVG' ? fcfg.showLabel !== false : true)) {
      addLabel({
        time: left,
        price: top,
        text: label,
        color: labelColor,
        anchor: 'above',
        offset: 8,
        priority: 5,
      })
    }

    if (showMidline && midline != null) {
      const mid = cleanValue(midline)
      if (mid != null) {
        const midSeries = api.addSeries(c => c.addLineSeries({
          title: '',
          color: withAlpha(color, 0.40),
          lineWidth: 1,
          lineStyle: LineStyle.Dotted,
          lastValueVisible: false,
          priceLineVisible: false,
        }))
        if (midSeries) midSeries.setData([{ time: left, value: mid }, { time: right, value: mid }])
      }
    }
  }
  const tBoxes1 = typeof performance !== 'undefined' ? performance.now() : 0
  // eslint-disable-next-line no-console
  debugLog(`[ict_quantum] boxes: ${(tBoxes1 - tBoxes0).toFixed(2)}ms`)

  // ── Líneas estructurales ───────────────────────────────────────────────────
  const tLines0 = typeof performance !== 'undefined' ? performance.now() : 0
  const candidateLines = []
  for (const line of result.lines || []) {
    const start = cleanTime(line.start_time)
    const end = cleanTime(line.end_time)
    const price = cleanValue(line.price)
    if (start == null || end == null || price == null) continue
    if (!overlapsVisibleRange(start, end, visibleRange)) continue

    const type = line.type
    let visible = false
    let color = theme.neutral
    let width = 1
    let style = 'solid'
    let category = 'other'
    let maxEvents = Infinity

    if (type === 'BOS' || type === 'CHoCH') {
      if (scfg.showSwingStruct === false) continue
      visible = true
      color = type === 'CHoCH' ? theme.choch : theme.bull
      width = 2
      style = type === 'CHoCH' ? 'dashed' : 'solid'
      category = 'swing'
      maxEvents = scfg.maxHistoryEvents ?? 5
    } else if (type === 'INTERNAL_BOS' || type === 'INTERNAL_CHoCH') {
      if (scfg.showInternalStruct === false) continue
      visible = true
      color = type === 'INTERNAL_CHoCH' ? theme.choch : theme.bull
      width = 1.5
      style = 'dashed'
      category = 'internal'
      maxEvents = scfg.maxInternalEvents ?? 3
    } else if (type === 'FIB') {
      if (fibcfg.showFib === false) continue
      visible = true
      color = fibcfg.lineColor ?? theme.neutral
      width = 1
      style = 'dashed'
      category = 'fib'
    } else if (type === 'STRONG' || type === 'WEAK') {
      if (scfg.showStrongWeak === false) continue
      visible = true
      color = theme.neutral
      width = 1
      style = 'dashed'
      category = 'strongweak'
      maxEvents = scfg.maxSwingLabels ?? 10
    }

    if (!visible) continue
    candidateLines.push({ start, end, price, color, width, style, category, maxEvents, type, label: line.label, showLabel: line.show_label, direction: line.direction })
  }

  const sortedLines = sortByTimeDesc(candidateLines, 'start')
  const limitedLines = []
  const lineCounts = { swing: 0, internal: 0, fib: 0, strongweak: 0, other: 0 }
  for (const line of sortedLines) {
    const cat = line.category || 'other'
    if (lineCounts[cat] >= line.maxEvents) continue
    lineCounts[cat]++
    limitedLines.push(line)
  }
  ictDebug.renderedLines = { ...lineCounts }

  const swingMarkerTimes = new Set(
    (result.labels || [])
      .filter(lb => ['HH', 'HL', 'LH', 'LL'].includes(lb.text))
      .map(lb => cleanTime(lb.time))
      .filter(t => t != null)
  )

  for (const line of limitedLines) {
    const ls = api.addSeries(c => c.addLineSeries({
      title: '',
      color: line.color,
      lineWidth: line.width,
      lineStyle: lineStyleFromString(line.style),
      lastValueVisible: false,
      priceLineVisible: false,
    }))
    if (ls) ls.setData([{ time: line.start, value: line.price }, { time: line.end, value: line.price }])

    if (line.showLabel && line.label && line.type !== 'FIB') {
      addLabel({
        time: line.start,
        price: line.price,
        text: line.label,
        color: line.color,
        anchor: 'above',
        offset: 16,
        priority: line.type === 'BOS' || line.type === 'CHoCH' ? 10 : 7,
      })
    }

    if (line.type === 'FIB' && fibcfg.showLabels !== false) {
      addLabel({
        time: line.end,
        price: line.price,
        text: line.label || 'Fib',
        color: line.color,
        priority: 3,
      })
    }

    if ((line.type === 'STRONG' || line.type === 'WEAK') && line.start != null && !swingMarkerTimes.has(line.start)) {
      api.addMarker({
        time: line.start,
        position: line.direction === 'bull' ? 'belowBar' : 'aboveBar',
        color: line.direction === 'bull' ? theme.bull : theme.bear,
        shape: line.direction === 'bull' ? 'arrowUp' : 'arrowDown',
        size: 1,
      })
    }
  }
  const tLines1 = typeof performance !== 'undefined' ? performance.now() : 0
  // eslint-disable-next-line no-console
  debugLog(`[ict_quantum] lines: ${(tLines1 - tLines0).toFixed(2)}ms`)

  // ── Labels HH/HL/LH/LL ─────────────────────────────────────────────────────
  const tLabels0 = typeof performance !== 'undefined' ? performance.now() : 0
  if (scfg.showSwingLabels !== false) {
    const swingLabelTexts = new Set(['HH', 'HL', 'LH', 'LL'])
    const swingLabels = (result.labels || []).filter(lb => swingLabelTexts.has(lb.text) && (lb.style === 'label_down' || lb.style === 'label_up'))
    const recentLabels = sortByTimeDesc(swingLabels)
      .filter(lb => isInVisibleRange(lb.time, visibleRange))
      .slice(0, scfg.maxSwingLabels ?? 20)
    ictDebug.renderedSwingLabels = recentLabels.length
    for (const lb of recentLabels) {
      const time = cleanTime(lb.time)
      const price = cleanValue(lb.price)
      if (time == null || price == null) continue
      const isHigh = lb.style === 'label_down'
      const labelColor = isHigh ? theme.bear : theme.bull
      addLabel({
        time,
        price,
        text: lb.text,
        color: labelColor,
        anchor: isHigh ? 'above' : 'below',
        offset: 26,
        priority: 8,
      })
      api.addMarker({
        time,
        position: isHigh ? 'aboveBar' : 'belowBar',
        color: labelColor,
        shape: isHigh ? 'arrowDown' : 'arrowUp',
        size: 1,
      })
    }
  }
  const tLabels1 = typeof performance !== 'undefined' ? performance.now() : 0
  // eslint-disable-next-line no-console
  debugLog(`[ict_quantum] labels: ${(tLabels1 - tLabels0).toFixed(2)}ms`)

  // ── Señal activa (entry/SL/TP) ─────────────────────────────────────────────
  const tSignal0 = typeof performance !== 'undefined' ? performance.now() : 0
  const signal = result.signal
  if (signal && signal.direction !== 'none' && sigcfg.showSignal !== false) {
    const isLong = signal.direction === 'long'
    const color = isLong ? theme.bull : theme.bear
    const riskColor = cfg.colorBear || '#B71C1C'
    const rewardColor = cfg.colorBull || '#1B5E20'
    const entryPrice = cleanValue(signal.entry_price)
    const slPrice = cleanValue(signal.stop_loss)
    const tp1Price = cleanValue(signal.take_profit_1)
    const entryTime = cleanTime(signal.entry_time)
    const barInterval = barIntervalSeconds(allCandles)

    if (entryPrice != null && entryTime != null) {
        // Ancho fijo en barras: el borde derecho cae en `max(entrada, última
        // vela) + extensionBars`, nunca pegado al eje de precio. Los puntos a
        // futuro se emiten barra a barra porque el time scale solo apila en el
        // whitespace puntos CONSECUTIVOS (un punto suelto a futuro colapsa en
        // lastIndex+1 — "la pared"). Para que la extensión quepa en pantalla
        // se amplía el rightOffset del chart si el usuario pide más barras de
        // las que caben (mínimo 20, el default del chart).
        const rightAnchor = Math.max(entryTime, cleanTime(lastTime) ?? entryTime)
        const lastT = cleanTime(lastTime) ?? rightAnchor
        const extensionBars = sigcfg.extensionBars ?? 10
        let rightOffsetBars = 20
        try {
          const ts = chart?.timeScale?.()
          const current = ts?.options?.()?.rightOffset ?? 20
          const desired = Math.max(20, extensionBars + 1)
          if (desired > current) ts.applyOptions({ rightOffset: desired })
          rightOffsetBars = Math.max(current, desired)
        } catch {}
        const maxRight = lastT + barInterval * Math.max(1, rightOffsetBars - 1)
        const targetRight = Math.min(rightAnchor + barInterval * extensionBars, maxRight)
        const spanTimes = []
        for (let t = entryTime; t <= targetRight && spanTimes.length < 400; t += barInterval) spanTimes.push(t)
        // Línea horizontal dibujada con BoxFillSeries degenerada (upper ==
        // lower, sin relleno): una LineSeries normal se detiene en
        // lastIndex+1; así la línea acompaña a la caja hasta su borde.
        const addEdgeLine = (price, color, borderWidth, borderStyle) => {
          if (price == null || !Number.isFinite(price)) return
          const line = api.addCustomSeries(new BoxFillSeries(), {
            color: 'rgba(0,0,0,0)',
            fillColor: 'rgba(0,0,0,0)',
            lineWidth: 0,
            lastValueVisible: false,
            priceLineVisible: false,
            borderWidth,
            borderStyle,
            alpha: 1,
            autoscaleInfoProvider: () => null,
          })
          if (line) {
            line.setData(spanTimes.map(t => ({
              time: t, upper: price, lower: price, fillColor: 'transparent', color, alpha: 1, borderColor: color, borderWidth, borderStyle,
            })))
          }
        }

        // Caja roja de riesgo (entry -> SL) — relleno ~30%.
        if (slPrice != null) {
          const riskUpper = isLong ? entryPrice : slPrice
          const riskLower = isLong ? slPrice : entryPrice
          const riskBox = api.addCustomSeries(new BoxFillSeries(), {
            color: 'rgba(0,0,0,0)',
            fillColor: 'rgba(0,0,0,0)',
            lineWidth: 0,
            lastValueVisible: false,
            priceLineVisible: false,
            borderWidth: 1,
            borderStyle: 'solid',
            alpha: 0.30,
            autoscaleInfoProvider: () => null,
          })
          if (riskBox) {
            riskBox.setData(spanTimes.map(t => ({
              time: t, upper: riskUpper, lower: riskLower, fillColor: riskColor, color: riskColor, alpha: 0.30, borderColor: riskColor, borderWidth: 1, borderStyle: 'solid',
            })))
          }
          // Línea SL sólida roja.
          addEdgeLine(slPrice, riskColor, 1.5, 'solid')
        }

        // Caja verde de recompensa (entry -> TP1) — relleno ~30%.
        if (tp1Price != null) {
          const rewardUpper = isLong ? tp1Price : entryPrice
          const rewardLower = isLong ? entryPrice : tp1Price
          const rewardBox = api.addCustomSeries(new BoxFillSeries(), {
            color: 'rgba(0,0,0,0)',
            fillColor: 'rgba(0,0,0,0)',
            lineWidth: 0,
            lastValueVisible: false,
            priceLineVisible: false,
            borderWidth: 1,
            borderStyle: 'solid',
            alpha: 0.30,
            autoscaleInfoProvider: () => null,
          })
          if (rewardBox) {
            rewardBox.setData(spanTimes.map(t => ({
              time: t, upper: rewardUpper, lower: rewardLower, fillColor: rewardColor, color: rewardColor, alpha: 0.30, borderColor: rewardColor, borderWidth: 1, borderStyle: 'solid',
            })))
          }
          // TPs punteados verdes.
          const addTpLine = (price) => addEdgeLine(price, rewardColor, 1, 'dotted')
          if (sigcfg.showTP1 !== false) addTpLine(signal.take_profit_1)
          if (sigcfg.showTP2 !== false && signal.take_profit_2 != null) addTpLine(signal.take_profit_2)
          if (sigcfg.showTP3 !== false && signal.take_profit_3 != null) addTpLine(signal.take_profit_3)
          if (sigcfg.showTP4 !== false && signal.take_profit_4 != null) addTpLine(signal.take_profit_4)
          if (sigcfg.showTP5 !== false && signal.take_profit_5 != null) addTpLine(signal.take_profit_5)
        }

        addLabel({
          time: entryTime,
          price: entryPrice,
          text: isLong ? 'LONG' : 'SHORT',
          color,
          anchor: isLong ? 'below' : 'above',
          offset: 20,
          priority: 9,
        })
      }

      // SL/BE/TP como etiquetas en el eje de precio derecho (sin filtro de
      // visibilidad para que pinten en el primer ciclo de datos).
      const addPriceLabel = (price, title, labelColor) => {
        if (price == null || !Number.isFinite(price)) return
        api.addPriceLine({ price, title, color: labelColor, lineVisible: false, axisLabelVisible: true })
      }

      addPriceLabel(entryPrice, `BE ${signal.grade || (isLong ? 'LONG' : 'SHORT')}`, color)
      if (sigcfg.showSL !== false) {
        addPriceLabel(slPrice, 'SL', riskColor)
      }
      if (sigcfg.showTP1 !== false) addPriceLabel(signal.take_profit_1, `TP1 ${(signal.risk_reward_1 ?? 0).toFixed(1)}R`, rewardColor)
      if (sigcfg.showTP2 !== false && signal.take_profit_2 != null) addPriceLabel(signal.take_profit_2, `TP2 ${(signal.risk_reward_2 ?? 0).toFixed(1)}R`, rewardColor)
      if (sigcfg.showTP3 !== false && signal.take_profit_3 != null) addPriceLabel(signal.take_profit_3, `TP3 ${(signal.risk_reward_3 ?? 0).toFixed(1)}R`, rewardColor)
      if (sigcfg.showTP4 !== false && signal.take_profit_4 != null) addPriceLabel(signal.take_profit_4, `TP4 ${(signal.risk_reward_4 ?? 0).toFixed(1)}R`, rewardColor)
      if (sigcfg.showTP5 !== false && signal.take_profit_5 != null) addPriceLabel(signal.take_profit_5, `TP5 ${(signal.risk_reward_5 ?? 0).toFixed(1)}R`, rewardColor)

      if (entryTime != null) {
        api.addMarker({
          time: entryTime,
          position: isLong ? 'belowBar' : 'aboveBar',
          color: riskColor,
          shape: isLong ? 'arrowUp' : 'arrowDown',
          size: cfg.markerSize ?? 2,
        })
      }
    }
  const tSignal1 = typeof performance !== 'undefined' ? performance.now() : 0
  // eslint-disable-next-line no-console
  debugLog(`[ict_quantum] signal: ${(tSignal1 - tSignal0).toFixed(2)}ms`)

  // ── Etiquetas de texto plano en canvas (una sola capa, frame a frame) ──────
  const tLabelOverlay0 = typeof performance !== 'undefined' ? performance.now() : 0
  if (Object.keys(labelsByTime).length > 0) {
    const labelSeries = api.addCustomSeries(new LabelOverlaySeries(), {
      color: 'transparent',
      lineWidth: 0,
      lastValueVisible: false,
      priceLineVisible: false,
      crosshairMarkerVisible: false,
      autoscaleInfoProvider: () => null,
      priceScaleId: 'right',
      plaqueStyle,
    })
    if (labelSeries) {
      const labelData = Object.keys(labelsByTime)
        .map(t => ({ time: Number(t), labels: labelsByTime[t] }))
        .sort((a, b) => a.time - b.time)
      labelSeries.setData(labelData)
    }
  }
  const tLabelOverlay1 = typeof performance !== 'undefined' ? performance.now() : 0
  // eslint-disable-next-line no-console
  debugLog(`[ict_quantum] label overlay: ${(tLabelOverlay1 - tLabelOverlay0).toFixed(2)}ms`)

  // Guarda anti-mutación de velas
  if (candleSeries && typeof candleSeries.data === 'function') {
    const after = candleSeries.data().length
    if (after !== (candleSeries.__ictqCandleCountBeforeRender ?? after)) {
      // eslint-disable-next-line no-console
      debugWarn('[ict_quantum] candle series length changed during render:', candleSeries.__ictqCandleCountBeforeRender, '->', after)
    }
  }

  const t1 = typeof performance !== 'undefined' ? performance.now() : 0
  // eslint-disable-next-line no-console
  debugLog(`[ict_quantum] render done: ${(t1 - t0).toFixed(2)}ms`)

  if (typeof window !== 'undefined') {
    window.__ICT_DEBUG__ = ictDebug
  }
}

export default {
  id: 'ict_quantum',
  name: 'ICT / SMC Quantum',
  version: '1.0.0',
  defaultConfig,
  detect,
  render,
  BadgeComponent,
  PanelComponent,
}
